import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('NotificationService', 'user-approval-notifications');

/**
 * ⚠️ CRITICAL: Resend Email Service - USER APPROVAL WORKFLOW ONLY
 *
 * This service sends emails via Resend EXCLUSIVELY for the user approval workflow:
 * 1. Welcome email when admin approves a new user
 * 2. Alert email to admins when new user signs up
 *
 * DO NOT USE THIS FOR:
 * - Borrower communications (use gmailService instead)
 * - Artemis Quote Rate delivery (owned by the Gmail-backed quote Edge Function)
 * - Pre-approval letters (use gmailService)
 * - Any external client communications
 * - General system notifications
 *
 * See docs/EMAIL_ARCHITECTURE.md for complete guidelines.
 */

export type NotificationType = 'user_approved' | 'admin_new_user_pending' | 'admin_compensation_request' | 'compensation_approved' | 'compensation_rejected';

export interface NotificationResponse {
  success: boolean;
  emailId?: string;
  message: string;
  error?: string;
}

/**
 * Service for sending user approval workflow notifications via Resend
 */
class NotificationService {
  /**
   * Send welcome email to newly approved user
   *
   * @param userId - ID of the user who was just approved
   * @param role - Role assigned to the user (e.g., 'Loan Officer', 'Admin')
   * @param firstName - User's first name for personalization
   */
  async sendUserApprovedEmail(
    userId: string,
    role: string,
    firstName?: string
  ): Promise<NotificationResponse> {
    try {
      log.info('Sending user approved email', { userId, role });

      // Get current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session) {
        throw new Error('User not authenticated');
      }

      // Call the Edge Function
      const { data, error } = await supabase.functions.invoke('send-notification', {
        body: {
          userId,
          notificationType: 'user_approved',
          data: {
            firstName,
            role,
            loginUrl: `${window.location.origin}/internal`
          }
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        log.error('Failed to send user approved email', { error: error.message }, error);
        return {
          success: false,
          message: 'Failed to send approval notification',
          error: error.message
        };
      }

      log.info('User approved email sent successfully', {
        userId,
        emailId: data.emailId
      });

      return data;

    } catch (error) {
      log.error('Error in sendUserApprovedEmail', {
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);

      return {
        success: false,
        message: 'Failed to send approval notification',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Send notification to all admins when new user signs up
   *
   * @param newUserData - Data about the new pending user
   */
  async sendAdminNewUserPendingEmail(newUserData: {
    userId: string;
    fullName: string;
    email: string;
    signupTime: string;
  }): Promise<NotificationResponse[]> {
    try {
      log.info('Sending admin new user pending notifications', {
        newUserEmail: newUserData.email
      });

      // Get all admin user IDs
      const adminIds = await this.getAdminUserIds();

      if (adminIds.length === 0) {
        log.warn('No admin users found to notify');
        return [{
          success: false,
          message: 'No admin users found',
          error: 'No administrators configured in the system'
        }];
      }

      // Get current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session) {
        throw new Error('User not authenticated');
      }

      // Send notification to each admin
      const promises = adminIds.map(async (adminId) => {
        const { data, error } = await supabase.functions.invoke('send-notification', {
          body: {
            userId: adminId,
            notificationType: 'admin_new_user_pending',
            data: {
              fullName: newUserData.fullName,
              email: newUserData.email,
              signupTime: newUserData.signupTime,
              approvalUrl: `${window.location.origin}/internal?section=manage-users`
            }
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`
          }
        });

        if (error) {
          log.error('Failed to send admin notification', {
            adminId,
            error: error.message
          }, error);
          return {
            success: false,
            message: 'Failed to send admin notification',
            error: error.message
          };
        }

        return data;
      });

      const results = await Promise.all(promises);

      log.info('Admin notifications sent', {
        totalAdmins: adminIds.length,
        successCount: results.filter(r => r.success).length
      });

      return results;

    } catch (error) {
      log.error('Error in sendAdminNewUserPendingEmail', {
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);

      return [{
        success: false,
        message: 'Failed to send admin notifications',
        error: error instanceof Error ? error.message : 'Unknown error'
      }];
    }
  }

  /**
   * Send notification to all admins when a loan officer requests compensation changes
   *
   * @param requestData - Data about the compensation change request
   */
  async sendCompensationChangeRequestEmail(requestData: {
    requesterName: string;
    requesterEmail: string;
    currentBankedComp: string | null;
    requestedBankedComp: string | null;
    currentMaxComp: string | null;
    requestedMaxComp: string | null;
    justification: string;
    effectiveDate: string;
  }): Promise<NotificationResponse[]> {
    try {
      log.info('Sending compensation change request notifications to admins', {
        requesterEmail: requestData.requesterEmail
      });

      // Get all admin user IDs
      const adminIds = await this.getAdminUserIds();

      if (adminIds.length === 0) {
        log.warn('No admin users found to notify about compensation request');
        return [{
          success: false,
          message: 'No admin users found',
          error: 'No administrators configured in the system'
        }];
      }

      // Get current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session) {
        throw new Error('User not authenticated');
      }

      // Send notification to each admin
      const promises = adminIds.map(async (adminId) => {
        const { data, error } = await supabase.functions.invoke('send-notification', {
          body: {
            userId: adminId,
            notificationType: 'admin_compensation_request',
            data: {
              requesterName: requestData.requesterName,
              requesterEmail: requestData.requesterEmail,
              currentBankedComp: requestData.currentBankedComp,
              requestedBankedComp: requestData.requestedBankedComp,
              currentMaxComp: requestData.currentMaxComp,
              requestedMaxComp: requestData.requestedMaxComp,
              justification: requestData.justification,
              effectiveDate: requestData.effectiveDate,
              requestedAt: new Date().toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              }),
              approvalUrl: `${window.location.origin}/internal?section=company-settings&tab=compensation`
            }
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`
          }
        });

        if (error) {
          log.error('Failed to send compensation request notification', {
            adminId,
            error: error.message
          }, error);
          return {
            success: false,
            message: 'Failed to send compensation request notification',
            error: error.message
          };
        }

        return data;
      });

      const results = await Promise.all(promises);

      log.info('Compensation request notifications sent', {
        totalAdmins: adminIds.length,
        successCount: results.filter(r => r.success).length
      });

      return results;

    } catch (error) {
      log.error('Error in sendCompensationChangeRequestEmail', {
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);

      return [{
        success: false,
        message: 'Failed to send compensation request notifications',
        error: error instanceof Error ? error.message : 'Unknown error'
      }];
    }
  }

  /**
   * Send notification to loan officer when their compensation request is approved
   *
   * @param requestData - Data about the approved compensation request
   */
  async sendCompensationApprovedEmail(requestData: {
    userId: string;
    firstName: string;
    previousBankedComp: string | null;
    newBankedComp: string | null;
    previousMaxComp: string | null;
    newMaxComp: string | null;
    approvedBy: string;
  }): Promise<NotificationResponse> {
    try {
      log.info('Sending compensation approved notification', {
        userId: requestData.userId
      });

      // Get current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase.functions.invoke('send-notification', {
        body: {
          userId: requestData.userId,
          notificationType: 'compensation_approved',
          data: {
            firstName: requestData.firstName,
            previousBankedComp: requestData.previousBankedComp,
            newBankedComp: requestData.newBankedComp,
            previousMaxComp: requestData.previousMaxComp,
            newMaxComp: requestData.newMaxComp,
            approvedBy: requestData.approvedBy,
            approvedAt: new Date().toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            }),
            dashboardUrl: `${window.location.origin}/internal`
          }
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        log.error('Failed to send compensation approved notification', {
          userId: requestData.userId,
          error: error.message
        }, error);
        return {
          success: false,
          message: 'Failed to send compensation approved notification',
          error: error.message
        };
      }

      log.info('Compensation approved notification sent', {
        userId: requestData.userId,
        emailId: data.emailId
      });

      return data;

    } catch (error) {
      log.error('Error in sendCompensationApprovedEmail', {
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);

      return {
        success: false,
        message: 'Failed to send compensation approved notification',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Send notification to loan officer when their compensation request is rejected
   *
   * @param requestData - Data about the rejected compensation request
   */
  async sendCompensationRejectedEmail(requestData: {
    userId: string;
    firstName: string;
    requestedBankedComp: string | null;
    requestedMaxComp: string | null;
    rejectedBy: string;
    rejectionNotes?: string;
  }): Promise<NotificationResponse> {
    try {
      log.info('Sending compensation rejected notification', {
        userId: requestData.userId
      });

      // Get current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase.functions.invoke('send-notification', {
        body: {
          userId: requestData.userId,
          notificationType: 'compensation_rejected',
          data: {
            firstName: requestData.firstName,
            requestedBankedComp: requestData.requestedBankedComp,
            requestedMaxComp: requestData.requestedMaxComp,
            rejectedBy: requestData.rejectedBy,
            rejectionNotes: requestData.rejectionNotes || 'No specific reason provided.',
            rejectedAt: new Date().toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            }),
            dashboardUrl: `${window.location.origin}/internal`
          }
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        log.error('Failed to send compensation rejected notification', {
          userId: requestData.userId,
          error: error.message
        }, error);
        return {
          success: false,
          message: 'Failed to send compensation rejected notification',
          error: error.message
        };
      }

      log.info('Compensation rejected notification sent', {
        userId: requestData.userId,
        emailId: data.emailId
      });

      return data;

    } catch (error) {
      log.error('Error in sendCompensationRejectedEmail', {
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);

      return {
        success: false,
        message: 'Failed to send compensation rejected notification',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get all admin user IDs for notifications
   * @private
   */
  private async getAdminUserIds(): Promise<string[]> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id')
        .contains('roles', ['admin'])
        .eq('employment_status', 'active');

      if (error) {
        log.error('Failed to fetch admin users', { error: error.message }, error);
        return [];
      }

      return data?.map(u => u.id) || [];
    } catch (error) {
      log.error('Error fetching admin users', {
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);
      return [];
    }
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
